import type { BoxBounds, NormalisedColorType, Point } from 'ag-charts-core';
import { boxContains, isBetweenAngles, toRadians } from 'ag-charts-core';
import type { AgSelectionContainment } from 'ag-charts-types';

import type { FromToMotionPropFn, FromToMotionPropFnContext, NodeUpdateState } from '../../../motion/fromToMotion';
import { BBox } from '../../../scene/bbox';
import type { Group } from '../../../scene/group';
import type { Node } from '../../../scene/node';
import { Sector } from '../../../scene/shape/sector';
import { Transformable } from '../../../scene/transformable';
import { boxOverlapsSector } from '../../../scene/util/sector';
import type { Marker } from '../../marker/marker';
import type { PickNodesInBBoxPredicate, SeriesNodePickMatch } from '../series';

type AnimatableSectorDatum = {
    radius: number;
    innerRadius: number;
    outerRadius: number;
    startAngle: number;
    endAngle: number;
    sectorFormat: {
        fill?: NormalisedColorType;
        stroke?: string;
    };
};

type ScaleFn = { convert(x: number): number };
export function preparePieSeriesAnimationFunctions(
    initialLoad: boolean,
    rotationDegrees: number,
    scaleFn: ScaleFn,
    oldScaleFn: ScaleFn
) {
    const scale: [number, number] = [scaleFn.convert(0), scaleFn.convert(1)];
    const oldScale: [number, number] = [oldScaleFn.convert(0), oldScaleFn.convert(1)];
    const rotation = Math.PI / -2 + toRadians(rotationDegrees);
    const phase = initialLoad ? 'initial' : 'update';

    const scaleToNewRadius = ({ radius }: AnimatableSectorDatum) => {
        return { innerRadius: scale[0], outerRadius: scale[0] + (scale[1] - scale[0]) * radius };
    };

    const scaleToOldRadius = ({ radius }: AnimatableSectorDatum) => {
        return { innerRadius: oldScale[0], outerRadius: oldScale[0] + (oldScale[1] - oldScale[0]) * radius };
    };

    const fromFn: FromToMotionPropFn<AnimatableSectorDatum, Sector<AnimatableSectorDatum>, any> = (
        sect,
        datum,
        status: NodeUpdateState,
        { prevFromProps }: FromToMotionPropFnContext<Sector>
    ) => {
        // Default to starting from current state.
        let { startAngle, endAngle, innerRadius, outerRadius } = sect;
        let { fill, stroke } = datum.sectorFormat;

        if (status === 'updated' && sect.previousDatum == null) {
            status = 'added';
        }

        if (status === 'unknown' || (status === 'added' && !prevFromProps)) {
            // Start of animation (full new data) - sweep in.
            startAngle = rotation;
            endAngle = rotation;
            innerRadius = datum.innerRadius;
            outerRadius = datum.outerRadius;
        } else if (status === 'added' && prevFromProps) {
            startAngle = prevFromProps.endAngle ?? rotation;
            endAngle = prevFromProps.endAngle ?? rotation;
            innerRadius = prevFromProps.innerRadius ?? datum.innerRadius;
            outerRadius = prevFromProps.outerRadius ?? datum.outerRadius;
        }

        if (status === 'added' && !initialLoad) {
            const radii = scaleToOldRadius(datum);
            innerRadius = radii.innerRadius;
            outerRadius = radii.outerRadius;
        }

        if (status === 'updated') {
            fill = sect.fill ?? fill;
            stroke = (typeof sect.stroke === 'string' ? sect.stroke : undefined) ?? stroke;
        }

        const animatableFill = typeof fill === 'string' ? { fill } : {};

        return { startAngle, endAngle, innerRadius, outerRadius, stroke, phase, ...animatableFill };
    };
    const toFn: FromToMotionPropFn<AnimatableSectorDatum, Sector<AnimatableSectorDatum>, any> = (
        _sect: Sector,
        datum: AnimatableSectorDatum,
        status: NodeUpdateState,
        { prevLive }: FromToMotionPropFnContext<Sector>
    ) => {
        // Default to moving to target state.
        let { startAngle, endAngle, innerRadius, outerRadius } = datum;
        const { stroke, fill } = datum.sectorFormat;

        if (status === 'removed' && prevLive) {
            startAngle = prevLive.unsafeDatum?.endAngle;
            endAngle = prevLive.unsafeDatum?.endAngle;
        } else if (status === 'removed' && !prevLive) {
            startAngle = rotation;
            endAngle = rotation;
        }

        if (status === 'removed') {
            const radii = scaleToNewRadius(datum);
            innerRadius = radii.innerRadius;
            outerRadius = radii.outerRadius;
        }

        const animatableFill = typeof fill === 'string' ? { fill } : {};

        return { startAngle, endAngle, outerRadius, innerRadius, stroke, ...animatableFill };
    };

    type D = { radius: number };
    type T = { size: number; phase?: typeof phase };
    const innerCircleFromFn: FromToMotionPropFn<D, Marker<D>, T> = (node, _) => {
        return { size: node.unsafePreviousDatum?.radius ?? node.size ?? 0, phase };
    };
    const innerCircleToFn: FromToMotionPropFn<D, Marker<D>, T> = (_, datum) => {
        return { size: datum.radius ?? 0 };
    };

    return { nodes: { toFn, fromFn }, innerCircle: { fromFn: innerCircleFromFn, toFn: innerCircleToFn } };
}

export function resetPieSelectionsFn(_node: Sector, datum: AnimatableSectorDatum) {
    return {
        startAngle: datum.startAngle,
        endAngle: datum.endAngle,
        innerRadius: datum.innerRadius,
        outerRadius: datum.outerRadius,
        stroke: datum.sectorFormat.stroke,
    };
}

type SectorVariables = {
    readonly innerRadius: number;
    readonly outerRadius: number;
    readonly startAngle: number;
    readonly endAngle: number;
};
type SectorSceneNode = SectorVariables & { readonly datum: any };
type SectorSeries = {
    centerX: number;
    centerY: number;
    contentGroup: Group;
    getItemNodes(): SectorSceneNode[];
};

export function pickByMatchingAngle(series: SectorSeries, point: Point): SeriesNodePickMatch | undefined {
    const dy = point.y - series.centerY;
    const dx = point.x - series.centerX;
    const angle = Math.atan2(dy, dx);
    const sectors: SectorSceneNode[] = series.getItemNodes();
    for (const sector of sectors) {
        if (sector.datum.missing === true) continue;

        if (isBetweenAngles(angle, sector.startAngle, sector.endAngle)) {
            const radius = Math.hypot(dx, dy);
            let distance = 0;
            if (radius < sector.innerRadius) {
                distance = sector.innerRadius - radius;
            } else if (radius > sector.outerRadius) {
                distance = radius - sector.outerRadius;
            }
            return { datum: sector.datum, distance, target: series.contentGroup };
        }
    }
    return undefined;
}

export function pickSectorsInBBoxPredicate(series: {
    properties: { selection: { containment: AgSelectionContainment } };
    contentGroup: Group;
}): PickNodesInBBoxPredicate {
    const unreachable = (a: never): never => a;
    const containment = series.properties.selection.containment;
    switch (containment) {
        case 'any':
            return (selectionBox: BoxBounds, node: Node<unknown>): boolean => {
                if (node instanceof Sector) {
                    const seriesSelectionBox = Transformable.fromCanvas(
                        series.contentGroup,
                        new BBox(selectionBox.x, selectionBox.y, selectionBox.width, selectionBox.height)
                    );
                    // boxOverlapsSector assumes an origin-centred sector, so shift the box into the
                    // sector's local frame to honour a non-zero centre offset (selectedOffset).
                    seriesSelectionBox.x -= node.centerX;
                    seriesSelectionBox.y -= node.centerY;
                    return boxOverlapsSector(seriesSelectionBox, node);
                }
                return false;
            };
        case 'all':
            return (selectionBox: BoxBounds, node: Node<unknown>): boolean => {
                if (node instanceof Sector) {
                    const { x, y, width, height } = Transformable.toCanvas(series.contentGroup, node.getBBox());
                    return boxContains(selectionBox, x, y, width, height);
                }
                return false;
            };
        default:
            return unreachable(containment);
    }
}
